'use client';

import type { CSSProperties, ReactElement } from 'react';
import {
  ACCENT,
  AXIS_LABEL_COLOR,
  AXIS_LABEL_FONT_SIZE,
  CORNER_LABEL_COLOR,
  CORNER_LABEL_FONT_SIZE,
  MIDLINE_STROKE,
  MIDLINE_WIDTH,
  PLOT_BORDER,
  PLOT_BORDER_WIDTH,
  PLOT_CORNER_RADIUS,
  PLOT_FILL,
  POINT_HALO_FILL,
  POINT_HALO_RADIUS,
  POINT_LABEL_COLOR,
  POINT_LABEL_FONT_SIZE,
  POINT_RADIUS,
  TITLE_COLOR,
  TITLE_FONT_SIZE,
  TITLE_Y,
  VIEW_HEIGHT,
  VIEW_WIDTH,
} from './quadrant-constants';
import { QuadrantLabelAnchor, type QuadrantLayout } from './quadrant-types';

const QUADRANT_SVG_XMLNS = 'http://www.w3.org/2000/svg';
const QUADRANT_SVG_MAX_WIDTH = '100%';
const QUADRANT_SVG_HEIGHT_AUTO = 'auto';
const QUADRANT_SVG_DISPLAY_BLOCK = 'block';
const QUADRANT_SVG_MARGIN_AUTO = '0 auto';
const QUADRANT_TITLE_ANCHOR = QuadrantLabelAnchor.Middle;

const quadrantSvgStyle: CSSProperties = {
  maxWidth: QUADRANT_SVG_MAX_WIDTH,
  height: QUADRANT_SVG_HEIGHT_AUTO,
  display: QUADRANT_SVG_DISPLAY_BLOCK,
  margin: QUADRANT_SVG_MARGIN_AUTO,
};

interface QuadrantCanvasProps {
  layout: QuadrantLayout;
  title: string | null;
}

export function QuadrantCanvas({ layout, title }: QuadrantCanvasProps): ReactElement {

  return (
      <svg
        viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
        width={VIEW_WIDTH}
        height={VIEW_HEIGHT}
        xmlns={QUADRANT_SVG_XMLNS}
        style={quadrantSvgStyle}
      >
        {title ? (
          <text
            x={VIEW_WIDTH / 2}
            y={TITLE_Y}
            textAnchor={QUADRANT_TITLE_ANCHOR}
            fontSize={TITLE_FONT_SIZE}
            fill={TITLE_COLOR}
          >
            {title}
          </text>
        ) : null}

        <rect
          x={layout.plotLeft}
          y={layout.plotTop}
          width={layout.plotWidth}
          height={layout.plotHeight}
          rx={PLOT_CORNER_RADIUS}
          fill={PLOT_FILL}
          stroke={PLOT_BORDER}
          strokeWidth={PLOT_BORDER_WIDTH}
        />

        <line
          x1={layout.midX}
          y1={layout.plotTop}
          x2={layout.midX}
          y2={layout.plotTop + layout.plotHeight}
          stroke={MIDLINE_STROKE}
          strokeWidth={MIDLINE_WIDTH}
        />
        <line
          x1={layout.plotLeft}
          y1={layout.midY}
          x2={layout.plotLeft + layout.plotWidth}
          y2={layout.midY}
          stroke={MIDLINE_STROKE}
          strokeWidth={MIDLINE_WIDTH}
        />

        {layout.cornerLabels.map((cornerLabel) => (
          <text
            key={cornerLabel.text}
            x={cornerLabel.x}
            y={cornerLabel.y}
            textAnchor={cornerLabel.anchor}
            fontSize={CORNER_LABEL_FONT_SIZE}
            fill={CORNER_LABEL_COLOR}
          >
            {cornerLabel.text}
          </text>
        ))}

        {layout.axisLabels.map((axisLabel) => (
          <text
            key={`${axisLabel.text}-${axisLabel.rotation}-${axisLabel.x}-${axisLabel.y}`}
            x={axisLabel.x}
            y={axisLabel.y}
            textAnchor={axisLabel.anchor}
            fontSize={AXIS_LABEL_FONT_SIZE}
            fill={AXIS_LABEL_COLOR}
            transform={`rotate(${axisLabel.rotation} ${axisLabel.x} ${axisLabel.y})`}
          >
            {axisLabel.text}
          </text>
        ))}

        {layout.points.map((plottedPoint) => (
          <g key={plottedPoint.debugIdentity}>
            <circle
              cx={plottedPoint.centerX}
              cy={plottedPoint.centerY}
              r={POINT_HALO_RADIUS}
              fill={POINT_HALO_FILL}
            />
            <circle
              cx={plottedPoint.centerX}
              cy={plottedPoint.centerY}
              r={POINT_RADIUS}
              fill={ACCENT}
            />
            <text
              x={plottedPoint.labelX}
              y={plottedPoint.labelY}
              textAnchor={plottedPoint.labelAnchor}
              fontSize={POINT_LABEL_FONT_SIZE}
              fill={POINT_LABEL_COLOR}
            >
              {plottedPoint.label}
            </text>
          </g>
        ))}
      </svg>
  );
}
